import React, { useMemo, useState, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { useDebouncedValue } from '@mantine/hooks';
import { GetFunctionsQuery } from '@hud/portal-dtos/webapp-function.dto';
import useFunctions from '../../../hooks/useFunctions';
import Page from '../../../components/Page/Page';
import FunctionsTable from '../../../components/FunctionsTable/FunctionsTable';
import useTimeFrame from '../../../hooks/useTimeFrame';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import { type Runtime, type ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { useFunctionFilters } from '../../../hooks/useFunctionFilter';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
} from '../../../utils';
import { Events } from '../../../analytics';

type FunctionsPageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    showUnnamed: boolean;
    files: string[];
    services: number[];
    runtimes: Runtime[];
  };

export default function FunctionsPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const functionFilters = useFunctionFilters();

  const { userPreferredServices } = useUserPreferences();

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<FunctionsPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'normalizedRuns.value',
      desc: true,

      showUnnamed: false,
      files: [],
      services: [],
      runtimes: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  useEffect(() => {
    if (pageState.services.length === 0) {
      setPageState({ services: userPreferredServices ?? [] });
    }
  }, [userPreferredServices]);

  const [totalPages, setTotalPages] = useState<number>(0);

  const referralBreadcrumbs: BreadcrumbItem[] | undefined = useMemo(() => {
    if (!functionFilters) return;
    return [
      {
        title: 'Functions',
        to: `/explore/functions?${functionFilters}`,
        nowrap: true,
      },
    ];
  }, [functionFilters]);

  useEffect(() => {
    document.title = 'Functions • Hud';
  }, []);

  const clearAllFilters = () => {
    setPageState({
      showUnnamed: false,
      files: [],
      services: [],
      runtimes: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });
  };

  const handleShowUnnamedChange = (value: boolean) => {
    setPageState({ showUnnamed: value, page: 1 });
  };

  const handleFilteredFilePathsChange = (value: string[]) => {
    setPageState({ files: value, page: 1 });
  };

  const handleFilteredServicesChange = (value: number[]) => {
    setPageState({ services: value, page: 1 });
  };

  const handleFilteredRuntimesChange = (value: Runtime[]) => {
    setPageState({ runtimes: value, page: 1 });
  };

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      environment: environment?.name,
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'functions_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'functions_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'functions_error_rate_filter_toggled',
    );
  };

  const functionsQueryParams = useMemo<GetFunctionsQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetFunctionsQuery['orderBy'],
      includeGhost: true, // todo: from user
      includeUnnamed: pageState.showUnnamed,
      timeFrame,
      filePaths: pageState.files,
      serviceIds: pageState.services,
      environmentName: environment?.name,
      runtimes: pageState.runtimes,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      pageState.showUnnamed,
      timeFrame,
      pageState.files,
      pageState.services,
      pageState.runtimes,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
      environment?.name,
    ],
  );

  const [debouncedFunctionsQueryParams] = useDebouncedValue(
    functionsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );

  const functionsQuery = useFunctions(debouncedFunctionsQueryParams);

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      const totalPages = Math.ceil(functionsQuery.data.countFiltered / DEFAULT_PAGE_SIZE);
      setTotalPages(totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionsQuery.data, functionsQuery.isSuccess]);

  // analytics events
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('functions_viewed', {
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.name]);

  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('functions_searched', {
      search: tableState.search,
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  useEffect(() => {
    analytics.capture('functions_sorted', {
      column_name: tableState.orderBy?.key ?? '',
      column_order: tableState.orderBy?.sortOrder ?? 'asc',
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy.key, tableState.orderBy.sortOrder]);

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      analytics.capture('functions_paginated', {
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      analytics.capture('functions_hide_unnamed_functions_toggled', {
        toggle_value: pageState.showUnnamed,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.showUnnamed]);

  if (functionsQuery.isError) {
    if (!isAxiosError(functionsQuery.error)) {
      return (
        <div>
          <div>Something went wrong</div>
        </div>
      );
    }
    const error = functionsQuery.error.response?.data;
    console.error(error);
    return <div>{error?.message ?? 'Something went wrong'}</div>;
  }

  return (
    <Page
      header="Functions"
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <FunctionsTable
        timeFrame={timeFrame}
        fullPageTable
        functions={functionsQuery.data?.functions ?? []}
        loading={functionsQuery.isLoading}
        total={functionsQuery.data?.total}
        filtered={functionsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        showUnnamed={pageState.showUnnamed}
        onShowUnnamedChange={handleShowUnnamedChange}
        filteredFilePaths={pageState.files}
        onFilteredFilePathsChange={handleFilteredFilePathsChange}
        filteredServices={pageState.services}
        onFilteredServicesChange={handleFilteredServicesChange}
        filteredRuntimes={pageState.runtimes}
        onFilteredRuntimesChange={handleFilteredRuntimesChange}
        referralBreadcrumbs={referralBreadcrumbs}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        onClearAllFilters={clearAllFilters}
      />
    </Page>
  );
}
